import { cn } from "@ui/lib";
import type { ComponentProps } from "react";

/**
 * The site's buttons, all links. `primary` is the one thing to do on a screen, `secondary` the
 * quiet alternative; the `night` pair is for dark sections. A further action is a text link
 * (`mkLink`), not another button.
 */
export const mkButton = ({
	variant = "primary",
	size = "md",
	className,
}: {
	variant?: "primary" | "secondary" | "night" | "night-secondary";
	size?: "sm" | "md" | "lg";
	className?: string;
} = {}) =>
	cn(
		"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium outline-none transition-[background-color,color,transform] duration-(--duration-hover) ease-(--ease-out-ui) active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-(--mk-signal) focus-visible:ring-offset-2",
		size === "sm" && "h-8 px-3.5 text-[13px]",
		size === "md" && "h-11 px-5 text-[15px]",
		size === "lg" && "h-13 px-7 text-[17px]",
		variant === "primary" &&
			"bg-(--mk-ink) text-(--mk-white) hover:bg-(--mk-ink-2)",
		variant === "secondary" &&
			"bg-(--mk-mist) text-(--mk-ink) hover:bg-(--mk-mist-2)",
		variant === "night" &&
			"bg-(--mk-on-night) text-(--mk-night) hover:bg-(--mk-white) focus-visible:ring-offset-(--mk-night)",
		variant === "night-secondary" &&
			"bg-(--mk-night-3) text-(--mk-on-night) hover:bg-(--mk-night-2) focus-visible:ring-offset-(--mk-night)",
		className,
	);

/** A text link with a chevron: "Learn more", "How voice works". */
export const mkLink = (className?: string) =>
	cn(
		"group/link inline-flex items-center gap-1 font-medium text-(--mk-signal-text) outline-none hover:underline hover:underline-offset-4 focus-visible:underline",
		className,
	);

/** The chevron after a text link, nudged forward on hover. */
export function Chevron({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 16 16"
			aria-hidden="true"
			className={cn(
				"size-3.5 transition-transform duration-(--duration-hover) ease-(--ease-out-ui) group-hover/link:translate-x-0.5",
				className,
			)}
		>
			<path
				d="m6 3.5 4.5 4.5L6 12.5"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export function WhatsAppGlyph({ className }: ComponentProps<"svg">) {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden="true"
			className={cn("size-4", className)}
		>
			<path
				fill="currentColor"
				d="M12.04 2a9.9 9.9 0 0 0-8.5 15l-1.4 5.1 5.3-1.4A9.9 9.9 0 1 0 12.04 2Zm0 18.1a8.2 8.2 0 0 1-4.2-1.15l-.3-.18-3.1.82.83-3.04-.2-.31a8.2 8.2 0 1 1 6.97 3.86Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.55.12-.16.25-.63.8-.78.96-.14.16-.29.18-.53.06a6.7 6.7 0 0 1-3.3-2.88c-.25-.43.25-.4.71-1.33.08-.16.04-.3-.02-.43-.06-.12-.55-1.33-.76-1.82-.2-.48-.4-.41-.55-.42h-.47a.9.9 0 0 0-.65.3 2.74 2.74 0 0 0-.86 2.04 4.77 4.77 0 0 0 1 2.53 10.9 10.9 0 0 0 4.18 3.7c1.56.67 2.17.73 2.95.62.48-.07 1.46-.6 1.67-1.18.2-.58.2-1.08.14-1.18-.06-.1-.22-.16-.47-.29Z"
			/>
		</svg>
	);
}
